import { useState, useEffect } from 'react';
import SEO, { PAGE_SEO } from '../components/SEO';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { Trophy, Sparkles, Medal, Crown, User, Calendar, CalendarDays, Award } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Leaderboard() {
  const { user } = useAuth();
  const [leaderboard, setLeaderboard] = useState([]);
  const [stats, setStats] = useState(null);
  const [userRank, setUserRank] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('overall');

  let token = null;
  try {
    token = localStorage.getItem('skinuse_token');
  } catch (e) {}
  const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch leaderboard for selected period
        const leaderboardRes = await fetch(`${BACKEND_URL}/api/leaderboard?limit=50&period=${period}`);
        if (leaderboardRes.ok) {
          setLeaderboard(await leaderboardRes.json());
        }

        // Fetch stats
        const statsRes = await fetch(`${BACKEND_URL}/api/leaderboard/stats`);
        if (statsRes.ok) {
          setStats(await statsRes.json());
        }

        // Fetch user rank if logged in
        if (user) {
          const rankRes = await fetch(`${BACKEND_URL}/api/leaderboard/user/${user.user_id}`, {
            headers
          });
          if (rankRes.ok) {
            setUserRank(await rankRes.json());
          }
        }
      } catch (error) {
        console.error('Leaderboard fetch error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user, period]);

  const getRankIcon = (rank) => {
    if (rank === 1) return <Crown className="w-5 h-5" />;
    if (rank === 2 || rank === 3) return <Medal className="w-5 h-5" />;
    return null;
  };

  const getRankStyle = (rank) => {
    if (rank === 1) return 'rank-gold';
    if (rank === 2) return 'rank-silver';
    if (rank === 3) return 'rank-bronze';
    return 'bg-secondary text-muted-foreground';
  };

  const tabs = [
    { id: 'daily', label: 'Today', icon: Calendar },
    { id: 'weekly', label: 'This Week', icon: CalendarDays },
    { id: 'overall', label: 'All Time', icon: Award },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background" data-testid="leaderboard-page">
      <SEO {...PAGE_SEO.leaderboard} url="/leaderboard" />
      <Navbar />
      
      <main className="flex-1 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-gold/10 rounded-sm mb-4">
              <Trophy className="w-4 h-4 text-gold" />
              <span className="text-xs font-semibold uppercase tracking-wider text-gold">Rankings</span>
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl font-semibold mb-4">SkinPoints Leaderboard</h1>
            <p className="text-lg text-muted-foreground max-w-xl mx-auto">
              See who's leading the beauty knowledge race. Earn points by reading, quizzing, and engaging!
            </p>
          </div>

          {/* Stats Banner */}
          {stats && (
            <div className="grid grid-cols-3 gap-4 mb-8">
              <Card className="bg-card border-none shadow-card text-center p-4">
                <p className="text-2xl font-bold text-gold">{stats.total_users}</p>
                <p className="text-xs text-muted-foreground">Total Members</p>
              </Card>
              <Card className="bg-card border-none shadow-card text-center p-4">
                <p className="text-2xl font-bold text-gold">{stats.total_points_distributed?.toLocaleString()}</p>
                <p className="text-xs text-muted-foreground">Points Earned</p>
              </Card>
              <Card className="bg-card border-none shadow-card text-center p-4">
                <p className="text-2xl font-bold text-gold">{stats.top_scorer?.skin_points || 0}</p>
                <p className="text-xs text-muted-foreground">Top Score</p>
              </Card>
            </div>
          )}

          {/* Period Tabs */}
          <div className="flex justify-center mb-8">
            <div className="inline-flex bg-secondary/50 p-1 rounded-lg">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setPeriod(tab.id)}
                    className={`flex items-center gap-2 px-6 py-3 rounded-md text-sm font-semibold transition-all ${
                      period === tab.id 
                        ? 'bg-gold text-black shadow-md' 
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                    data-testid={`tab-${tab.id}`}
                  >
                    <Icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* User Rank Card */}
          {user && userRank && period === 'overall' && (
            <Card className="bg-gold/10 border-none mb-8" data-testid="user-rank-card">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-full bg-primary flex items-center justify-center">
                      {user.picture ? (
                        <img src={user.picture} alt="" className="w-14 h-14 rounded-full object-cover" />
                      ) : (
                        <User className="w-7 h-7 text-primary-foreground" />
                      )}
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Your Rank</p>
                      <p className="font-serif text-3xl font-semibold">#{userRank.rank}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-2 justify-end">
                      <Sparkles className="w-5 h-5 text-gold" />
                      <span className="font-serif text-3xl font-semibold text-gold">{user.skin_points || 0}</span>
                    </div>
                    <p className="text-sm text-muted-foreground">SkinPoints</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Top 3 Podium */}
          {!loading && leaderboard.length >= 3 && (
            <div className="flex justify-center items-end gap-4 mb-12" data-testid="podium">
              {/* 2nd Place */}
              <div className="text-center">
                <div className="w-20 h-20 mx-auto mb-3 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                  {leaderboard[1]?.picture ? (
                    <img src={leaderboard[1].picture} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <User className="w-10 h-10 text-gray-400" />
                  )}
                </div>
                <div className="w-24 h-24 rank-silver rounded-t-sm flex flex-col items-center justify-center">
                  <Medal className="w-6 h-6 mb-1" />
                  <span className="text-2xl font-bold">2</span>
                </div>
                <p className="font-medium mt-2 truncate max-w-[100px]">{leaderboard[1]?.name}</p>
                <p className="text-sm text-gold font-semibold">{leaderboard[1]?.points || leaderboard[1]?.skin_points} pts</p>
              </div>

              {/* 1st Place */}
              <div className="text-center -mx-2">
                <div className="w-24 h-24 mx-auto mb-3 rounded-full bg-gold/20 flex items-center justify-center overflow-hidden ring-4 ring-gold">
                  {leaderboard[0]?.picture ? (
                    <img src={leaderboard[0].picture} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <User className="w-12 h-12 text-gold" />
                  )}
                </div>
                <div className="w-28 h-32 rank-gold rounded-t-sm flex flex-col items-center justify-center">
                  <Crown className="w-8 h-8 mb-1" />
                  <span className="text-3xl font-bold">1</span>
                </div>
                <p className="font-semibold mt-2 truncate max-w-[120px]">{leaderboard[0]?.name}</p>
                <p className="text-sm text-gold font-semibold">{leaderboard[0]?.points || leaderboard[0]?.skin_points} pts</p>
              </div>

              {/* 3rd Place */}
              <div className="text-center">
                <div className="w-20 h-20 mx-auto mb-3 rounded-full bg-amber-100 flex items-center justify-center overflow-hidden">
                  {leaderboard[2]?.picture ? (
                    <img src={leaderboard[2].picture} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <User className="w-10 h-10 text-amber-600" />
                  )}
                </div>
                <div className="w-24 h-20 rank-bronze rounded-t-sm flex flex-col items-center justify-center">
                  <Medal className="w-6 h-6 mb-1" />
                  <span className="text-2xl font-bold">3</span>
                </div>
                <p className="font-medium mt-2 truncate max-w-[100px]">{leaderboard[2]?.name}</p>
                <p className="text-sm text-gold font-semibold">{leaderboard[2]?.points || leaderboard[2]?.skin_points} pts</p>
              </div>
            </div>
          )}

          {/* Full Leaderboard */}
          <Card className="bg-card border-none shadow-card" data-testid="full-leaderboard">
            <CardHeader className="flex flex-row items-center justify-between">
              <h2 className="font-serif text-xl font-semibold">
                {period === 'daily' ? "Today's Rankings" : period === 'weekly' ? "This Week's Rankings" : "All Time Rankings"}
              </h2>
              {period !== 'overall' && (
                <span className="text-xs text-muted-foreground">Points earned this {period === 'daily' ? 'day' : 'week'}</span>
              )}
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-4">
                  {[...Array(10)].map((_, i) => (
                    <div key={i} className="flex items-center gap-4">
                      <Skeleton className="w-10 h-10 rounded-full" />
                      <Skeleton className="h-4 flex-1" />
                      <Skeleton className="w-20 h-4" />
                    </div>
                  ))}
                </div>
              ) : leaderboard.length === 0 ? (
                <div className="text-center py-12">
                  <Trophy className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">
                    {period === 'daily' ? "No points earned today yet. Be the first!" :
                     period === 'weekly' ? "No points earned this week yet. Start reading!" :
                     "No rankings yet. Be the first to earn SkinPoints!"}
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {leaderboard.map((rankedUser) => (
                    <div 
                      key={rankedUser.user_id} 
                      className={`flex items-center gap-4 p-4 rounded-sm transition-colors ${
                        user?.user_id === rankedUser.user_id ? 'bg-gold/10 ring-1 ring-gold/30' : 'hover:bg-secondary'
                      }`}
                      data-testid={`rank-${rankedUser.rank}`}
                    >
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${getRankStyle(rankedUser.rank)}`}>
                        {getRankIcon(rankedUser.rank) || rankedUser.rank}
                      </div>
                      <div className="w-10 h-10 rounded-full bg-secondary overflow-hidden flex items-center justify-center">
                        {rankedUser.picture ? (
                          <img src={rankedUser.picture} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <User className="w-5 h-5 text-muted-foreground" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">
                          {rankedUser.name}
                          {user?.user_id === rankedUser.user_id && (
                            <span className="ml-2 text-xs text-gold">(You)</span>
                          )}
                        </p>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Sparkles className="w-4 h-4 text-gold" />
                        <span className="font-semibold text-gold">{rankedUser.points || rankedUser.skin_points}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
}
